import type { Map as MapboxMap } from 'mapbox-gl';
import type { SceneSystem } from './SceneSystem';
import * as THREE from 'three';
import { pauseClock } from '../core/pause';

export class CameraSystem {
  readonly map: MapboxMap;
  private scene: SceneSystem;
  private activeIntervals: number[] = [];
  private activeTimeouts: number[] = [];

  constructor(map: MapboxMap, scene: SceneSystem) {
    this.map = map;
    this.scene = scene;
  }

  // Converts world [x, z] to mapbox [lng, lat]
  private toLngLat(worldCoord: [number, number]): [number, number] {
    return this.scene.worldToLngLat(new THREE.Vector3(worldCoord[0], 0, worldCoord[1]));
  }

  flyTo(params: { center: [number, number]; zoom: number; pitch?: number; bearing?: number; duration: number }): Promise<void> {
    return new Promise((resolve) => {
      const lngLat = this.toLngLat(params.center);
      this.map.flyTo({
        center: lngLat,
        zoom: params.zoom,
        pitch: params.pitch ?? 65,
        bearing: params.bearing ?? this.map.getBearing(),
        duration: params.duration,
        essential: true,
      });
      const t = window.setTimeout(resolve, params.duration);
      this.activeTimeouts.push(t);
    });
  }

  follow(getPosition: () => [number, number] | null, getBearing: () => number | null, duration: number, opts?: { pitch?: number; zoom?: number }): Promise<void> {
    return new Promise((resolve) => {
      let smoothBearing = this.map.getBearing();

      const interval = window.setInterval(() => {
        if (pauseClock.paused) return;
        const pos = getPosition();
        const bearing = getBearing();
        if (pos && bearing !== null) {
          let diff = bearing - smoothBearing;
          if (diff > 180) diff -= 360;
          if (diff < -180) diff += 360;
          smoothBearing += diff * 0.05;

          const lngLat = this.toLngLat(pos);
          this.map.easeTo({
            center: lngLat,
            zoom: opts?.zoom ?? 16.5,
            pitch: opts?.pitch ?? 68,
            bearing: smoothBearing,
            duration: 800,
            easing: (t: number) => t * (2 - t),
          });
        }
      }, 500);
      this.activeIntervals.push(interval);

      // Pausable: the follow holds for `duration` of unpaused time.
      pauseClock.wait(duration).then(() => {
        clearInterval(interval);
        resolve();
      });
    });
  }

  orbit(params: { center: [number, number]; zoom: number; pitch?: number; startBearing: number; endBearing: number; duration: number }): Promise<void> {
    return new Promise((resolve) => {
      const lngLat = this.toLngLat(params.center);
      const startBearing = params.startBearing;
      const endBearing = params.endBearing;
      const startTime = Date.now();
      const duration = params.duration;

      const interval = window.setInterval(() => {
        if (pauseClock.paused) return;
        const elapsed = Date.now() - startTime;
        const t = Math.min(elapsed / duration, 1);
        const eased = t * (2 - t);
        const bearing = startBearing + (endBearing - startBearing) * eased;

        this.map.easeTo({
          center: lngLat,
          zoom: params.zoom,
          pitch: params.pitch ?? 65,
          bearing,
          duration: 80,
          easing: (x: number) => x,
        });

        if (t >= 1) {
          clearInterval(interval);
          resolve();
        }
      }, 60);
      this.activeIntervals.push(interval);

      const t = window.setTimeout(() => {
        clearInterval(interval);
        resolve();
      }, duration + 200);
      this.activeTimeouts.push(t);
    });
  }

  getBearing(): number {
    return this.map.getBearing();
  }

  cancelAll(): void {
    this.activeIntervals.forEach(clearInterval);
    this.activeTimeouts.forEach(clearTimeout);
    this.activeIntervals = [];
    this.activeTimeouts = [];
  }

  dispose(): void {
    this.cancelAll();
  }
}
